import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { 
  getProjects, 
  createProject, 
  deleteProject as apiDeleteProject,
  // Project type is defined locally below
  CreateProjectData
} from '../../../services/projects';
import { ApiError, ApiErrorType } from '../../../services/api';

// Project interface for the component
export interface Project {
  id: string;
  name: string;
  location: string;
  status: 'New' | 'Analyzed' | 'Monitoring';
  lastUpdated: string;
  members?: any[];
}

// Function to convert API project to component project
const mapApiProjectToProject = (apiProject: any): Project => {
  // Handle both nested and flat location structures
  let locationString = '';
  
  if (apiProject.location) {
    // Nested location structure
    locationString = `${apiProject.location.address}, ${apiProject.location.city}, ${apiProject.location.state}`;
  } else if (apiProject.address) {
    // Flat location structure
    const parts = [];
    if (apiProject.address) parts.push(apiProject.address);
    if (apiProject.city) parts.push(apiProject.city);
    if (apiProject.state) parts.push(apiProject.state);
    if (apiProject.zipCode) parts.push(apiProject.zipCode);
    locationString = parts.join(', ');
  }
  
  return {
    id: apiProject.id,
    name: apiProject.name,
    location: locationString,
    status: apiProject.status,
    lastUpdated: apiProject.lastUpdated,
    members: apiProject.members
  };
};

export const useProjects = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    if (!user?.sub) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const apiProjects = await getProjects();
      // Convert API projects to component projects
      const mappedProjects = apiProjects.map(mapApiProjectToProject);
      setProjects(mappedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      let errorMessage = 'Failed to load projects. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NETWORK_ERROR:
            errorMessage = 'Network error. Please check your connection.';
            break;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
          // Handle other error types as needed
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Create a new project
  const addProject = async (projectData: {
    name: string;
    location: string;
    coordinates?: { latitude: number; longitude: number };
    addressComponents?: any;
    businessType?: string;
    locationType?: 'new' | 'existing';
  }): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Adding project with data:', projectData);
      
      let address, city, state, zipCode;
      
      // Use address components if available
      if (projectData.addressComponents) {
        const components = projectData.addressComponents;
        address = components.formatted_street || '';
        city = components.locality || '';
        state = components.administrative_area_level_1 || '';
        zipCode = components.postal_code || '';
      } else {
        // Fall back to parsing the location string
        const locationParts = projectData.location.split(',').map(part => part.trim());
        address = locationParts[0] || '';
        city = locationParts.length > 1 ? locationParts[1] : '';
        state = locationParts.length > 2 ? locationParts[2] : '';
        zipCode = locationParts.length > 3 ? locationParts[3] : '';
      }
      
      // Create project data with both flattened structure and structured location details
      const createData: CreateProjectData = {
        name: projectData.name,
        address, // Top-level address field
        city,
        state,
        zipCode,
        coordinates: projectData.coordinates ? {
          latitude: projectData.coordinates.latitude,
          longitude: projectData.coordinates.longitude
        } : undefined,
        status: 'New' as 'New', // Type assertion to match the expected literal type
        industry: projectData.businessType || 'entertainment', // Use selected business type or default
        businessType: projectData.businessType as 'Entertainment' | 'Restaurant' | 'Daycare' | 'Printing Services' | 'Storage' | 'Other' || 'Entertainment', // Set businessType field with proper typing
        
        // Add Google Places location details if available
        location_details: projectData.addressComponents && projectData.coordinates ? {
          place_id: projectData.addressComponents.place_id || '',
          formatted_address: projectData.location,
          address_components: {
            street_number: projectData.addressComponents.street_number || '',
            route: projectData.addressComponents.route || '',
            neighborhood: projectData.addressComponents.neighborhood || '',
            city: projectData.addressComponents.locality || '',
            county: projectData.addressComponents.administrative_area_level_2 || '',
            state: projectData.addressComponents.administrative_area_level_1 || '',
            postal_code: projectData.addressComponents.postal_code || '',
            country: projectData.addressComponents.country || ''
          },
          latitude: projectData.coordinates.latitude,
          longitude: projectData.coordinates.longitude
        } : undefined
      };
      
      console.log('Formatted project data for API:', createData);
      console.log('Business type:', projectData.businessType);
      console.log('Location type:', projectData.locationType);
      
      // Call API to create project
      const createdProject = await createProject(createData);
      
      // Add to projects list
      setProjects([...projects, mapApiProjectToProject(createdProject)]);
      return true;
    } catch (err) {
      console.error('Error creating project:', err);
      let errorMessage = 'Failed to create project. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.VALIDATION_ERROR:
            errorMessage = 'Invalid project data. Please check your inputs.';
            break;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
          // Handle other error types as needed
        }
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a project
  const deleteProject = async (projectId: string): Promise<boolean> => {
    try {
      console.log(`Deleting project ${projectId}`);
      await apiDeleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      return true;
    } catch (err) {
      console.error(`Failed to delete project ${projectId}:`, err);
      let errorMessage = 'Failed to delete project. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NOT_FOUND:
            // If the project is not found, we can still remove it from the UI
            setProjects(projects.filter(project => project.id !== projectId));
            return true;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
          // Handle other error types as needed
        }
      }
      
      setError(errorMessage);
      return false;
    }
  };

  // Run analysis again
  const runProjectAnalysis = async (projectId: string): Promise<boolean> => {
    try {
      console.log(`Running analysis again for project ${projectId}`);
      
      // In a real implementation, this would call an API to run the analysis again
      // For example:
      // await updateProject(projectId, { status: 'Analyzing' });
      
      // For now, just update the status locally
      const updatedProjects = projects.map(project => {
        if (project.id === projectId) {
          return {
            ...project,
            status: 'New' as const, // Use const assertion to ensure it's the correct type
            lastUpdated: new Date().toISOString()
          };
        }
        return project;
      });
      
      setProjects(updatedProjects);
      return true;
    } catch (err) {
      console.error(`Failed to run analysis again for project ${projectId}:`, err);
      let errorMessage = 'Failed to run analysis. Please try again.';
      
      if (err instanceof ApiError) {
        switch (err.type) {
          case ApiErrorType.UNAUTHORIZED:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case ApiErrorType.NOT_FOUND:
            errorMessage = 'Project not found. It may have been deleted.';
            break;
          case ApiErrorType.SERVER_ERROR:
            errorMessage = 'Server error. Please try again later.';
            break;
        }
      }
      
      setError(errorMessage);
      return false;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'New';
    
    const date = new Date(dateString);
    
    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return 'New';
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Load projects on component mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    isLoading,
    error,
    addProject,
    deleteProject,
    runProjectAnalysis,
    formatDate,
    refreshProjects: fetchProjects
  };
};
